import { DateRange } from '@uplift/types';
import { restClient } from '../../client';
import snakecaseKeys from 'snakecase-keys';
import { CollaborativeChartsReviewConfigFrequency } from './collaborativeChartsReviewConfig';


export enum CollaborativeChartsReviewStatus {
  pending = 'pending',
  submitted = 'submitted',
}

export type CollaborativeChartsReview = {
  id: string;
  matchId: string;
  period: DateRange;
  expireAt: string;
  submittedAt?: string;
  createdAt: string;
  sampleChartsAmountPercentage: number;
  reviewFrequency: CollaborativeChartsReviewConfigFrequency;
  feedback: string;
};


export enum ProviderChartItemType {
  psychiatryAppointmentSubmitted = 'psychiatry_appointment_submitted',
  billableNoteSubmitted = 'billable_note_submitted',
}

export type CollaborativeChartsReviewItem = {
  reviewId: string;
  resourceId: string;
  resourceType: ProviderChartItemType;
  completed: boolean;
  score: number;
  updatedAt: string;

  hpiClinicallyRelevant: string | null;
  intervalUpdatesDocumented: string | null;
  diagnosesSupportedByEvidence: string | null;
  medicationIndicationsDocumented: string | null;
  mdmConsistentWithGuidelines: string | null;
  nonPharmInterventionsDocumented: string | null;
  followupPlanDocumented: string | null;
  cptCodeAccurate: string | null;
  documentationSupportsContinuity: string | null;
  noteSubmittedWithin72Hrs: string | null;
};

export type ChartsReviewItemQuestionnaireFields = Pick<CollaborativeChartsReviewItem, (
  | 'hpiClinicallyRelevant'
  | 'intervalUpdatesDocumented'
  | 'diagnosesSupportedByEvidence'
  | 'medicationIndicationsDocumented'
  | 'mdmConsistentWithGuidelines'
  | 'nonPharmInterventionsDocumented'
  | 'followupPlanDocumented'
  | 'cptCodeAccurate'
  | 'documentationSupportsContinuity'
  | 'noteSubmittedWithin72Hrs'
)>;

export enum CollaborativeChartsReviewItemClinicalFlagReason {
  clientSafetyConcern = 'client_safety_concern',
  other = 'other',
}

export const collaborativeChartsReviewItemClinicalFlagReasonLabels: Record<CollaborativeChartsReviewItemClinicalFlagReason, string> = {
  [CollaborativeChartsReviewItemClinicalFlagReason.clientSafetyConcern]: 'Client Safety Concern',
  [CollaborativeChartsReviewItemClinicalFlagReason.other]: 'Other',
};

export type CollaborativeChartsReviewItemClinicalFlag = {
  reviewId: string;
  resourceId: string;
  reason: CollaborativeChartsReviewItemClinicalFlagReason;
  details: string;
};


export type AllCollaborativeChartsReviewsParams = {
  submitted: boolean;
};
export const getAllCollaborativeChartsReviews = (params?: AllCollaborativeChartsReviewsParams) => restClient.request<{
  data: CollaborativeChartsReview[];
}>('/api/v1/collaborative_physician/charts_review', {
  params: params ? snakecaseKeys(params) : undefined,
});


export const getCollaborativeChartsReviewForMatch = (matchId: string) => restClient.request<{
  data: CollaborativeChartsReview | null;
}>(`/api/v1/collaborative_physician/charts_review/for-match/${matchId}`);


export const getCollaborativeChartsReview = (reviewId: string) => restClient.request<{
  data: CollaborativeChartsReview | null;
}>(`/api/v1/collaborative_physician/charts_review/${reviewId}`);

export type UpdateCollaborativeChartsReviewParams = Pick<CollaborativeChartsReview, 'feedback'>;
export const updateCollaborativeChartsReview = (reviewId: string, params: UpdateCollaborativeChartsReviewParams) => restClient.request(`/api/v1/collaborative_physician/charts_review/${reviewId}`, {
  method: 'PATCH',
  data: params,
});


export const submitCollaborativeChartsReview = (reviewId: string) => restClient.request<{
  data: CollaborativeChartsReview;
}>(`/api/v1/collaborative_physician/charts_review/${reviewId}/submit`, { method: 'POST' });


export const getCollaborativeChartsReviewItems = (reviewId: string) => restClient.request<{
  data: CollaborativeChartsReviewItem[];
}>(`/api/v1/collaborative_physician/charts_review/${reviewId}/items`);


export const updateCollaborativeChartsReviewItemQuestionnaire = (reviewId: string, resourceId: string, fields: ChartsReviewItemQuestionnaireFields) => restClient.request(`/api/v1/collaborative_physician/charts_review/${reviewId}/items/${resourceId}/questionnaire`, {
  method: 'PUT',
  data: {
    ...snakecaseKeys(fields),
    note_submitted_within_72_hrs: fields.noteSubmittedWithin72Hrs,
  },
});


export const getCollaborativeChartsReviewClinicalFlags = (reviewId: string) => restClient.request<{
  data: CollaborativeChartsReviewItemClinicalFlag[];
}>(`/api/v1/collaborative_physician/charts_review/${reviewId}/clinical-flags`);


export type UpdateCollaborativeChartsReviewItemClinicalFlagFields = Pick<CollaborativeChartsReviewItemClinicalFlag, 'reason' | 'details'>;
export const updateCollaborativeChartsReviewItemClinicalFlag = (
  reviewId: string,
  resourceId: string,
  fields: UpdateCollaborativeChartsReviewItemClinicalFlagFields,
) => restClient.request(`/api/v1/collaborative_physician/charts_review/${reviewId}/items/${resourceId}/clinical-flag`, {
  method: 'PUT',
  data: {
    ...snakecaseKeys(fields),
  },
});

export const deleteCollaborativeChartsReviewItemClinicalFlag = (
  reviewId: string,
  resourceId: string,
) => restClient.request(`/api/v1/collaborative_physician/charts_review/${reviewId}/items/${resourceId}/clinical-flag`, {
  method: 'DELETE',
});
